/**
 * Moteur d'effets de contenu — les **18 types** de l'enum fermé de SPEC-CONTENU §2.3, avec
 * leur sémantique de cumul normative (§3.7).
 *
 * Deux moitiés, et la frontière est la seule chose à retenir :
 *
 *  - **10 types PERSISTANTS** (`unlock`, `potential_add`, `multiplier`, `assist`,
 *    `event_weight_mult_event`, `event_weight_mult_categorie`, `action_cost_mult`,
 *    `money_per_video`, `money_per_1k_subs_weekly`, `unsub_mult`) : ils ne modifient rien
 *    tout de suite, ils **répondent à une question posée plus tard** (« combien coûte cette
 *    action ? », « quel est mon potentiel de Qualité ? »). `collectModifiers` les agrège une
 *    fois par résolution en un `ActiveModifiers`.
 *  - **9 types PONCTUELS** (`subs_pct`, `subs_flat`, `gauge_delta`, `flag_add`,
 *    `flag_remove`, `strike_delta`, `pc_add`, `event_schedule`, `image_offset` ⟦A-67⟧) :
 *    `applyEffects` les applique à l'état, une fois.
 *
 * D'où viennent les effets persistants (deux sources, une seule vérité chacune) :
 *  1. les **skills possédés** — relus depuis `content/skills.json` via `state.skills` à
 *     chaque appel. Ils ne sont JAMAIS recopiés dans l'état : un nœud acheté n'a pas besoin
 *     d'être « rejoué » (et un respec les retire tout seuls).
 *  2. les **choix d'événements et les jalons** — non re-dérivables : `applyEffects` en pose
 *     la trace dans `state.content_effects.modifiers` (SPEC-MOTEUR §1.6, champ ajouté par
 *     cette tâche).
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucun `Math.random`, aucune horloge système. Le contenu
 * arrive **en paramètre** (arbitrage A25) et le temps est `state.clock_h` — les effets
 * temporaires expirent en **heures simulées éveillées**, jamais en temps réel.
 *
 * Arbitrage **A21** (déjà tranché) : le potentiel de Qualité/Diffusion vient des
 * `potential_add` sommés (SPEC-CONTENU), pas du `0,8 + 0,1 × nb de nœuds` de SPEC-MOTEUR
 * §4.3. Les bases restent Qualité 0,8 / Diffusion 1,0 / Portée 0,10.
 */
import { ASSIST_BOOL_PARAMS } from "../content/schema.js";
import { appliquerClamps, recalculerImage } from "./actions.js";
// ⟦SPEC-FIL §2.1 · M3.22⟧ Le clamp `[0;100]` de la Hype, lu chez celui qui le possède plutôt
// que recopié : deux bornes pour la même jauge divergeraient le jour où l'une bougerait.
import { clampHype } from "./fil.js";
// ⟦A-103 §1 · A-100 §6⟧ `poserStrike` était le seul émetteur du `warning`/`"strike"` — mort en
// production, aucun appelant hors tests. Le réutiliser ici plutôt que dupliquer sa forme de
// données : un strike posé par un choix d'événement (`strike_delta`) mérite le même
// télégraphiage qu'un strike posé « à la main ».
import { poserStrike } from "./gameover.js";
import { SYSTEMES_TOUS_ACTIFS } from "./menage.js";
import { H_STRIKE } from "./clock.js";
import { PLAFOND_ACCROCHE, PLAFOND_DIFFUSION, PLAFOND_PORTEE, PLAFOND_QUALITE, PLANCHER_DIFFUSION, PLANCHER_PORTEE, PLANCHER_QUALITE, } from "./formula.js";
import { ATELIER_STAGES } from "./types.js";
import { appliquerRelationRival, ciblesRelation } from "./rivaux.js";
import { appliquerTraitJauge } from "./traits.js";
// ─────────────────────────────────────────────────────────────────────────────
// Constantes normatives
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Plafond absolu de perte d'abonnés d'une application d'effets : **15 %** des abonnés
 * courants (PLAN §4.9, SPEC-MOTEUR §10.1 pas 8). Le schéma borne déjà chaque `subs_pct` à
 * −15 ; ce plafond-ci borne le **cumul** d'une même source (un événement à trois effets
 * négatifs ne peut pas dépasser −15 % à lui tout seul).
 *
 * Conséquence assumée, à signaler : le plafond s'applique AUSSI aux `subs_flat`, y compris
 * aux événements scriptés du tutoriel (SPEC-CONTENU §2.3 : `subs_flat` réservé à
 * `subs_max ≤ 1 000`). Sous ~7 abonnés, `floor(A × 0,15) = 0` : la perte est intégralement
 * absorbée. C'est la lecture littérale de « aucun effet ne retire plus de 15 % » ; aucun
 * contenu v1 n'utilise `subs_flat`, donc rien n'est neutralisé aujourd'hui.
 */
export const MAX_PERTE_ABONNES_PCT = 0.15;
/**
 * Bases des potentiels — arbitrage **A21**. `accroche` vaut 0 : son potentiel vient de la
 * table §4.4 (froid / tendance / evergreen…), et les `potential_add` d'Accroche sont donc un
 * **bonus additif** à cette table, pas un potentiel complet.
 */
export const POTENTIEL_BASE = {
    accroche: 0,
    qualite: 0.8,
    diffusion: 1.0,
    portee: PLANCHER_PORTEE, // 0,10
};
/** Plafonds de potentiel (SPEC-CONTENU §2.3/§3.7, PLAN §4.4). */
export const POTENTIEL_PLAFOND = {
    accroche: PLAFOND_ACCROCHE, // 2,8
    qualite: PLAFOND_QUALITE, // 2,0
    diffusion: PLAFOND_DIFFUSION, // 1,5
    portee: PLAFOND_PORTEE, // 0,35
};
/** Planchers : un `potential_add` négatif ne descend pas un potentiel sous son plancher. */
export const POTENTIEL_PLANCHER = {
    accroche: 0,
    qualite: PLANCHER_QUALITE,
    diffusion: PLANCHER_DIFFUSION,
    portee: PLANCHER_PORTEE,
};
/** Les 12 types d'effets persistants — ceux que `collectModifiers` agrège (⟦A-61⟧ : +1). */
export const EFFECT_TYPES_PERSISTANTS = [
    "unlock",
    "potential_add",
    "multiplier",
    "assist",
    "event_weight_mult_event",
    "event_weight_mult_categorie",
    "action_cost_mult",
    "money_per_video",
    "money_per_1k_subs_weekly",
    "sleep_recovery_add",
    "freelance_direction",
    "unsub_mult",
];
/** Les 9 types d'effets ponctuels — ceux que `applyEffects` applique à l'état (⟦A-67⟧ : +1). */
export const EFFECT_TYPES_PONCTUELS = [
    "subs_pct",
    "subs_flat",
    "gauge_delta",
    "flag_add",
    "flag_remove",
    "strike_delta",
    "pc_add",
    "event_schedule",
    "image_offset",
    "rival_relation_delta",
];
function clamp(v, min, max) {
    return Math.min(max, Math.max(min, v));
}
/** Multiplicateurs neutres — l'exhaustivité des 9 cibles est vérifiée par le compilateur. */
function multiplicateursNeutres() {
    return {
        xp: 1,
        rente: 1,
        rpm: 1,
        vues: 1,
        conversion: 1,
        revenus: 1,
        accroche: 1,
        qualite: 1,
        diffusion: 1,
    };
}
/** ⟦A-61⟧ Direction neutre — les 5 étapes à 0, donc `livraisonDirigee` = `livraison`. */
function directionVide() {
    return { ecriture: 0, tournage: 0, montage: 0, miniature: 0, promotion: 0 };
}
/** Assists vides — les 5 mini-jeux sont toujours présents (clé stable pour l'UI). */
function assistsVides() {
    return {
        fil_rouge: {},
        bonne_prise: {},
        couper_rythme: {},
        putaclic_o_metre: {},
        la_decoupe: {},
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// Le sidecar d'état (champ `content_effects`)
// ─────────────────────────────────────────────────────────────────────────────
/** Sidecar vide — la forme canonique de `state.content_effects`. */
export function contentEffectsVide() {
    return { modifiers: [], flags: {}, scheduled_events: [] };
}
/**
 * Lecture tolérante du sidecar : `state.content_effects` est **optionnel** (champ ajouté par
 * M1.8), `undefined` ⇔ sidecar vide. Ne mute pas l'état ; le tableau rendu peut être le
 * tableau vide partagé, donc **lecture seule**.
 */
export function contentEffects(state) {
    return state.content_effects ?? contentEffectsVide();
}
/** Idem, mais en écriture : matérialise le sidecar sur l'état (qui doit être un clone). */
function contentEffectsMutable(state) {
    state.content_effects ??= contentEffectsVide();
    return state.content_effects;
}
/** Un temporaire est expiré dès que `clock_h` a **atteint** son échéance (§3.7). */
function estExpire(m, clockH) {
    return m.expires_h !== null && clockH >= m.expires_h;
}
/**
 * Purge les modificateurs et les flags de contenu expirés (heures éveillées). À appeler au
 * pas 9 de la file de résolution (`resolve`, M1.17) — `collectModifiers` et
 * `hasContentFlag` ignorent de toute façon les entrées expirées, la purge n'est là que pour
 * que l'état ne gonfle pas. MUTE l'état (qui doit être un clone).
 */
export function purgerEffetsExpires(state) {
    const sidecar = state.content_effects;
    if (!sidecar)
        return;
    sidecar.modifiers = sidecar.modifiers.filter((m) => !estExpire(m, state.clock_h));
    for (const [flag, expires] of Object.entries(sidecar.flags)) {
        if (expires !== null && state.clock_h >= expires)
            delete sidecar.flags[flag];
    }
}
/** Un flag de CONTENU est-il posé et non expiré ? (Les flags moteur vivent dans `state.flags`.) */
export function hasContentFlag(state, flag) {
    const sidecar = state.content_effects;
    if (!sidecar || !(flag in sidecar.flags))
        return false;
    const expires = sidecar.flags[flag] ?? null;
    return expires === null || state.clock_h < expires;
}
/** Tous les flags de contenu actifs (non expirés) — pour les conditions d'événements (M1.15). */
export function contentFlagsActifs(state) {
    const sidecar = state.content_effects;
    if (!sidecar)
        return [];
    return Object.keys(sidecar.flags).filter((f) => hasContentFlag(state, f));
}
// ─────────────────────────────────────────────────────────────────────────────
// Normalisation : un effet persistant → une ligne de modificateur
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Traduit un effet **persistant** en ligne de modificateur datée. Rend `null` pour les
 * 8 types ponctuels (ils passent par `applyEffects`).
 *
 * `clockH` ne sert qu'aux `multiplier` avec `duree_h` : `expires_h = clock_h + duree_h`.
 */
export function toModifier(effect, clockH, source) {
    const base = { value: 0, expires_h: null, ...(source ? { source } : {}) };
    switch (effect.type) {
        case "unlock":
            return { ...base, type: effect.type, target: effect.capability, value: 1 };
        case "potential_add":
            return { ...base, type: effect.type, target: effect.factor, value: effect.value };
        case "multiplier":
            return {
                ...base,
                type: effect.type,
                target: effect.target,
                value: effect.value,
                expires_h: effect.duree_h === undefined ? null : clockH + effect.duree_h,
            };
        case "assist":
            return {
                ...base,
                type: effect.type,
                target: effect.minigame,
                sub: effect.param,
                value: effect.value,
            };
        case "event_weight_mult_event":
            return { ...base, type: effect.type, target: effect.event_id, value: effect.value };
        case "event_weight_mult_categorie":
            return { ...base, type: effect.type, target: effect.categorie, value: effect.value };
        case "action_cost_mult":
            return {
                ...base,
                type: effect.type,
                target: effect.action,
                sub: effect.ressource,
                value: effect.value,
                // ⟦A-9⟧ Même traitement que `multiplier` : absent ⇒ permanent (l'état neutre).
                expires_h: effect.duree_h === undefined ? null : clockH + effect.duree_h,
            };
        case "sleep_recovery_add":
            return { ...base, type: effect.type, target: effect.gauge, value: effect.value };
        // ⟦A-61⟧ `target` porte l'étape, ou le mot `toutes` — que `collectModifiers` déplie.
        case "freelance_direction":
            return { ...base, type: effect.type, target: effect.etape, value: effect.value };
        case "money_per_video":
            return { ...base, type: effect.type, target: effect.format, value: effect.value };
        case "money_per_1k_subs_weekly":
        case "unsub_mult":
            return { ...base, type: effect.type, target: "", value: effect.value };
        default:
            return null; // les 8 types ponctuels
    }
}
// ─────────────────────────────────────────────────────────────────────────────
// collectModifiers
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Lit les skills possédés (+ tout effet persistant porté par l'état) et rend l'agrégat.
 *
 * Ordre de lecture : skills d'abord (dans l'ordre du contenu), puis les lignes de l'état
 * (dans l'ordre de pose). Les produits et les sommes sont commutatifs — l'ordre n'a pas
 * d'incidence sur le résultat, il n'est fixé que pour la reproductibilité des flottants.
 *
 * Les effets **temporaires portés par un skill** (`multiplier` + `duree_h`) sont ignorés
 * ici : ils ont été datés à l'achat dans `state.content_effects.modifiers` par
 * `applyEffects(..., "skill")`. Sans cette règle, un temporaire de skill serait permanent.
 */
export function collectModifiers(state, content) {
    const potentialAdd = {
        accroche: 0,
        qualite: 0,
        diffusion: 0,
        portee: 0,
    };
    const multiplier = multiplicateursNeutres();
    // Les `multiplier` sont AUSSI ventilés par provenance : les propriétés permanentes de la
    // chaîne (skills possédés) d'un côté, les lignes portées par l'état (événements, jalons,
    // temporaires) de l'autre. Le §6.3 en a besoin : il FIGE la rente à la publication avec le
    // ×1,4 de SEO, mais un multiplicateur d'événement à `duree_h` doit pouvoir expirer — donc
    // s'appliquer au versement. Sans cette ventilation, SEO comptait deux fois (1,4² = 1,96).
    const multiplierSkills = multiplicateursNeutres();
    const multiplierEtat = multiplicateursNeutres();
    const unlocked = new Set();
    const assists = assistsVides();
    const direction = directionVide();
    const byCategory = {};
    const byId = {};
    const coutParAction = new Map();
    const moneyVideo = {
        short: 0,
        longue: 0,
        toutes: 0,
    };
    let moneyHebdoPar1k = 0;
    // ⟦A-9⟧ Neutre à 0 : sans matériel de vie, la nuit vaut exactement ce qu'elle valait.
    const sommeil = { energie: 0, sante_mentale: 0 };
    let unsub = 1;
    const agreger = (m, provenance = "etat") => {
        switch (m.type) {
            case "unlock":
                unlocked.add(m.target);
                break;
            case "potential_add":
                if (m.target in potentialAdd)
                    potentialAdd[m.target] += m.value;
                break;
            case "multiplier":
                if (m.target in multiplier) {
                    multiplier[m.target] *= m.value;
                    const ventile = provenance === "skill" ? multiplierSkills : multiplierEtat;
                    ventile[m.target] *= m.value;
                }
                break;
            case "assist": {
                const jeu = assists[m.target];
                const param = m.sub;
                if (!jeu || param === undefined)
                    break;
                // §3.7 : assists numériques additifs ; booléens = présence (jamais 2).
                if (ASSIST_BOOL_PARAMS.includes(param)) {
                    jeu[param] = Math.max(jeu[param] ?? 0, m.value);
                }
                else {
                    jeu[param] = (jeu[param] ?? 0) + m.value;
                }
                break;
            }
            case "event_weight_mult_event":
                byId[m.target] = (byId[m.target] ?? 1) * m.value;
                break;
            case "event_weight_mult_categorie":
                byCategory[m.target] = (byCategory[m.target] ?? 1) * m.value;
                break;
            case "action_cost_mult": {
                if (m.sub === undefined)
                    break;
                const cle = `${m.target}::${m.sub}`;
                coutParAction.set(cle, (coutParAction.get(cle) ?? 1) * m.value);
                break;
            }
            case "money_per_video":
                if (m.target in moneyVideo)
                    moneyVideo[m.target] += m.value;
                break;
            case "money_per_1k_subs_weekly":
                moneyHebdoPar1k += m.value;
                break;
            case "sleep_recovery_add":
                if (m.target === "energie" || m.target === "sante_mentale") {
                    sommeil[m.target] += m.value;
                }
                break;
            // ⟦A-61⟧ Cumul **additif** entre nœuds — c'est ce que dit l'unité : chacun apporte une
            //   part du chemin, et deux parts se suivent. `toutes` s'applique aux cinq étapes.
            case "freelance_direction": {
                const cibles = m.target === "toutes" ? ATELIER_STAGES : [m.target];
                for (const etape of cibles) {
                    if (etape in direction)
                        direction[etape] += m.value;
                }
                break;
            }
            case "unsub_mult":
                unsub *= m.value;
                break;
            default:
                break; // type ponctuel égaré dans les lignes : ignoré, jamais appliqué deux fois
        }
    };
    // 1. Les skills possédés — relus depuis le contenu, jamais recopiés dans l'état.
    const possedes = new Set(state.skills);
    for (const skill of content.skills) {
        if (!possedes.has(skill.id))
            continue;
        for (const effet of skill.effets) {
            const row = toModifier(effet, state.clock_h, skill.id);
            if (!row)
                continue;
            if (row.expires_h !== null)
                continue; // temporaire : daté à l'achat dans l'état
            agreger(row, "skill");
        }
        for (const a of skill.assists) {
            agreger({
                type: "assist",
                target: a.minigame,
                sub: a.param,
                value: a.value,
                expires_h: null,
                source: skill.id,
            });
        }
    }
    // 1bis. ⟦A-11 §4⟧ Les objets de boutique PERMANENTS possédés — relus depuis le contenu par
    //   leur id, exactement comme les skills, et pour la même raison : une seule source de
    //   vérité. Agrégés avec la provenance `"skill"` parce qu'ils sont, comme un nœud, une
    //   **propriété permanente de la chaîne** — c'est ce qui les fige correctement dans un
    //   `VideoRecord` (§6.3). Les consommables ne passent JAMAIS par ici : ils sont appliqués
    //   à l'achat (ponctuels) ou datés dans l'état (temporaires à `duree_h`).
    const objets = state.inventaire?.permanents;
    if (objets && objets.length > 0 && content.boutique) {
        const possedesObjets = new Set(objets);
        for (const objet of content.boutique) {
            if (!possedesObjets.has(objet.id))
                continue;
            for (const effet of objet.effets) {
                const row = toModifier(effet, state.clock_h, objet.id);
                if (!row)
                    continue;
                if (row.expires_h !== null)
                    continue; // temporaire : daté à l'achat dans l'état
                agreger(row, "skill");
            }
        }
    }
    // 1ter. ⟦A-60⟧ Le **matos préparé pour la prochaine production** — consommables en file
    //   dans `inventaire.prochaine_prod`. Seuls leurs `assists` sont lus ici : leurs `effets`
    //   (le prix en jauges) ont déjà été appliqués à l'achat, les relire les compterait deux
    //   fois. C'est l'exact miroir de la file de nuit, à un point d'application près.
    //
    //   La file est **vidée par `publier`** juste après le calcul des scores effectifs : c'est
    //   ce qui fait de l'aide une aide d'UNE vidéo, et c'est aussi ce qui la garde compatible
    //   avec l'anti-triche — le serveur régénère le pattern depuis l'état d'AVANT l'action, où
    //   l'objet est encore en file (`server/src/routes/actions.ts`).
    const prepares = state.inventaire?.prochaine_prod;
    if (prepares && prepares.length > 0 && content.boutique) {
        const enFile = new Set(prepares);
        for (const objet of content.boutique) {
            if (!enFile.has(objet.id))
                continue;
            for (const a of objet.assists ?? []) {
                agreger({
                    type: "assist",
                    target: a.minigame,
                    sub: a.param,
                    value: a.value,
                    expires_h: null,
                    source: objet.id,
                });
            }
        }
    }
    // 2. Les lignes portées par l'état (événements, jalons, temporaires de skills).
    for (const m of contentEffects(state).modifiers) {
        if (estExpire(m, state.clock_h))
            continue;
        agreger(m);
    }
    // 3. Potentiels : base A21 + somme des adds, borné plancher/plafond du facteur.
    const potential = {
        accroche: 0,
        qualite: 0,
        diffusion: 0,
        portee: 0,
    };
    for (const factor of Object.keys(potential)) {
        potential[factor] = clamp(POTENTIEL_BASE[factor] + potentialAdd[factor], POTENTIEL_PLANCHER[factor], POTENTIEL_PLAFOND[factor]);
    }
    return {
        potential,
        multiplier,
        multiplier_skills: multiplierSkills,
        multiplier_etat: multiplierEtat,
        unlocked,
        assists,
        freelanceDirection: direction,
        eventWeight: { byCategory, byId },
        actionCost(action, resource) {
            return coutParAction.get(`${action}::${resource}`) ?? 1;
        },
        money_per_video: moneyVideo.toutes,
        money_per_1k_subs_weekly: moneyHebdoPar1k,
        sleepRecovery: sommeil,
        unsub_mult: unsub,
        moneyPerVideo(format) {
            return moneyVideo.toutes + moneyVideo[format];
        },
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// Pont vers la table des coûts de `actions.ts` (§2.2)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Traduit un `Action` du moteur (§1.7, noms français) en `ActionId` du contenu (§2.1) — les
 * deux vocabulaires ne coïncident pas : le moteur a `produce_video` + `format`, le contenu a
 * `short` et `longue` ; le moteur a `sleep`, le contenu `dormir`. Rend `null` pour une action
 * qu'aucun `action_cost_mult` ne peut cibler.
 *
 * ✎ `video_jalon` est mappé sur `longue` : tous les jalons sont des longues
 * (SPEC-CONTENU §3.6, `format: z.enum(["longue"])`).
 */
export function actionIdDuContenu(action) {
    switch (action.type) {
        case "produce_video":
            return action.format === "short" ? "short" : "longue";
        case "video_jalon":
            return "longue";
        case "sleep":
            return "dormir";
        case "veille":
            return "veille";
        case "lire_commentaires":
            return "lire_commentaires";
        case "menage":
            return "menage";
        case "remaster":
            return "remaster";
        default:
            return null;
    }
}
/**
 * Callback à passer en 3e argument de `actionCost(action, state, mods)` (`actions.ts`) : il
 * applique les `action_cost_mult` de l'action considérée. Le `type: ActionType` reçu par le
 * callback ne suffit pas à distinguer short/longue, d'où la fermeture sur l'`Action`.
 */
export function costMods(mods, action) {
    const id = actionIdDuContenu(action);
    return (ressource) => (id === null ? 1 : mods.actionCost(id, ressource));
}
/** Jauge du contenu → champ de `GameState`. `inspiration` est traité à part (cartes). */
const CHAMP_DE_JAUGE = {
    energie: "energy",
    sante_mentale: "mental",
    reputation: "reputation",
    fidelite: "loyalty",
    argent: "money",
    momentum: "momentum",
};
/**
 * Applique une liste d'effets ponctuels (achat de skill, choix d'événement, jalon) à un
 * état. **L'entrée n'est jamais mutée** : on clone puis on travaille sur le clone.
 *
 * @param state  état de départ (jamais muté)
 * @param effects liste d'effets validée par Zod (les 18 types du §2.3)
 * @param source origine des effets — voir `EffectSourceKind`
 */
export function applyEffects(state, effects, source, options = {}) {
    return applyEffectsInPlace(structuredClone(state), effects, source, options);
}
/**
 * Même sémantique que `applyEffects`, mais **mute** la cible : c'est la forme utilisée dans
 * les hooks de résolution, où `ctx.state` est déjà le clone de travail (`actions.ts` §2.3).
 */
export function applyEffectsInPlace(target, effects, source, options = {}) {
    const capPct = options.cap_subs_loss_pct === undefined
        ? MAX_PERTE_ABONNES_PCT
        : options.cap_subs_loss_pct;
    const events = [];
    const log = [];
    const scheduled = [];
    const sys = options.systemes ?? SYSTEMES_TOUS_ACTIFS;
    const abonnesEntree = target.subscribers;
    let subsDelta = 0;
    let reputationDelta = 0;
    let pcGained = 0;
    let strikesAdded = 0;
    let cardsToDraw = 0;
    for (const effect of effects) {
        // — Les 10 types persistants : une ligne de modificateur (ou rien, cf. EffectSourceKind).
        const row = toModifier(effect, target.clock_h, options.source_id);
        if (row) {
            if (source !== "skill" || row.expires_h !== null) {
                contentEffectsMutable(target).modifiers.push(row);
            }
            continue;
        }
        // — Les 8 types ponctuels.
        switch (effect.type) {
            case "subs_pct":
                // « % des abonnés actuels » = les abonnés à l'ENTRÉE de l'application : deux effets
                // de la même source ne se composent pas (−10 % puis −10 % = −20 %, pas −19 %) ✎.
                subsDelta += Math.round((abonnesEntree * effect.value) / 100);
                break;
            case "subs_flat":
                subsDelta += effect.value;
                break;
            case "gauge_delta": {
                if (effect.gauge === "inspiration") {
                    if (effect.value > 0) {
                        cardsToDraw += Math.round(effect.value);
                    }
                    else {
                        // Défausse des cartes les plus anciennes (la main est une file, §2.2).
                        target.inspiration.splice(0, Math.min(target.inspiration.length, Math.round(-effect.value)));
                    }
                    break;
                }
                // **Avenant A-1 / arbitrage A37** : avant le Grand Saut, la jauge Argent est
                // INACTIVE — pas « à zéro », inactive. Un effet de contenu qui la déplace la
                // réactiverait par la bande. Mesuré par `simulate.ts` (M1.19) : 5 effets de perte
                // (`panne_materiel` −300/−900, `doxxing` −2000/−500, `strike_copyright` −50) ne
                // portent AUCUNE condition, alors que tous les effets de gain sont gated sur
                // `monetise` — seules les pertes fuyaient, et les runs arrivaient au Grand Saut
                // endettées. L'effet est ignoré, pas différé : ces euros n'existent pas encore.
                if (effect.gauge === "argent" && !target.flags.grand_saut_done)
                    break;
                // ⟦C60 · A-177⟧ Plus de dépense subie : une perte d'argent est ignorée quand les charges
                // sont coupées. Le gain, lui, passe.
                if (effect.gauge === "argent" && effect.value < 0 && !sys.charges)
                    break;
                // ⟦SPEC-FIL §8.3 · M3.22⟧ **La Hype, traitée à part et pas par étourderie.** Elle est
                // la seule jauge dont le champ est OPTIONNEL (`hype?: number`, `undefined ⇔ 0`) : la
                // passer par `CHAMP_DE_JAUGE` ferait `undefined + 3 = NaN`, et un NaN dans une jauge
                // se propage en silence à travers tout le reste de la résolution.
                //
                // ⚠️ **L'interrupteur du Fil n'est pas relu ici**, contrairement à `gagnerHype`
                // ⟦A-57 §4⟧, et c'est un choix qu'il faut pouvoir défendre : `applyEffectsInPlace` ne
                // reçoit pas le contenu (voir `ApplyEffectsOptions`), et le lui faire descendre
                // obligerait à toucher `boutique.ts`, `skills.ts`, `jalons.ts` et `events.ts` — quatre
                // fichiers de quatre chantiers. Ce que ça coûte est nul : Fil éteint, `bonusPorteeHype`
                // rend 0 et `vagueDePartage` rend `false`, donc une Hype écrite ici n'a **aucune
                // conséquence mécanique**. Elle ne serait qu'un nombre inerte, que la décroissance de
                // nuit ramènerait à 0 dès l'allumage. La propriété d'A-57 §4 qui compte — « une partie
                // ne démarre pas avec une Hype qu'elle n'a jamais gagnée » — tient toujours.
                if (effect.gauge === "hype") {
                    target.hype = clampHype((target.hype ?? 0) + effect.value);
                    break;
                }
                const champ = CHAMP_DE_JAUGE[effect.gauge];
                // ⟦A-4 §2⟧ Les traits de genre ne pèsent que sur les GAINS, et jamais sur le sommeil
                // — voir `traits.ts`. Un effet de contenu n'est jamais un sommeil : la nuit passe par
                // `sleep.ts`, qui n'emprunte pas ce chemin.
                const valeur = champ === "loyalty" || champ === "reputation" || champ === "mental"
                    ? appliquerTraitJauge(target.genre, champ, effect.value, { hors_sommeil: true })
                    : effect.value;
                target[champ] += valeur;
                if (effect.gauge === "reputation")
                    reputationDelta += valeur;
                break;
            }
            case "flag_add": {
                const flags = contentEffectsMutable(target).flags;
                flags[effect.flag] =
                    effect.expires_h === undefined ? null : target.clock_h + effect.expires_h;
                break;
            }
            case "flag_remove": {
                // Sans erreur si le flag est absent (§2.3).
                const sidecar = target.content_effects;
                if (sidecar)
                    delete sidecar.flags[effect.flag];
                break;
            }
            case "strike_delta": {
                // ⟦C60 · A-177⟧ Sans strikes, un `strike_delta` ne pose ni ne retire rien.
                if (!sys.strikes)
                    break;
                const strikes = target.counters.strikes;
                if (effect.value > 0) {
                    // ⟦A-103 §1⟧ `poserStrike` pousse le strike ET le `warning`/`"strike"` déjà lu par
                    // `client/src/game/journal.ts` (`actifs`, `fatal_a`, `expire_h`) — c'est le canal
                    // qu'⟦A-100 §6⟧ documentait comme mort faute d'appelant en production. Une boucle
                    // d'un tour par strike : un événement à trois strikes d'un coup télégraphie chacun
                    // avec son propre décompte d'actifs, au lieu d'un seul avertissement muet sur deux.
                    for (let i = 0; i < effect.value; i++)
                        poserStrike(target, events);
                    strikesAdded += effect.value;
                    log.push({
                        turn: target.turn,
                        clock_h: target.clock_h,
                        famille: "buzz",
                        text: effect.value === 1
                            ? `Un strike. Il expire dans ${H_STRIKE} h simulées.`
                            : `${effect.value} strikes d'un coup. Ça sent le bannissement.`,
                    });
                }
                else if (effect.value < 0) {
                    // Un strike retiré est un recours accepté : c'est le plus récemment posé (donc
                    // celui dont l'expiration est la plus lointaine) qui saute ✎.
                    for (let i = 0; i < -effect.value && strikes.length > 0; i++) {
                        const idxPlusRecent = strikes.indexOf(Math.max(...strikes));
                        strikes.splice(idxPlusRecent, 1);
                    }
                    strikesAdded += effect.value;
                }
                break;
            }
            case "pc_add":
                target.pc += effect.value;
                pcGained += effect.value;
                break;
            // ⟦A-67 · A-4 décision 6 · A33⟧ **L'offset d'image de chaîne, enfin écrivable par le
            // contenu.** `pause.ts` et `fil.ts` étaient jusqu'ici ses deux seuls écrivains ; un
            // événement qui voulait dire « image +3 » devait tricher en `gauge_delta reputation`
            // ⟦A-27⟧, et les dons caritatifs sont restés en intention faute de ce chemin ⟦A-63⟧.
            //
            // 🔑 `plafond` borne le RÉSULTAT, pas la valeur — c'est ce qui traduit « la charité ne
            // peut que RÉPARER une image abîmée » en règle de moteur : à `plafond: 0`, un don
            // ramène l'offset vers zéro et s'arrête là, quel que soit le nombre de dons. Sans
            // plafond, l'effet est un ±N ordinaire.
            //
            // ⚠️ `recalculerImage` est appelée juste après : l'offset est la cicatrice, l'image
            // est le visage, et c'est toujours la moyenne des alignements qui domine.
            case "image_offset": {
                // ⟦C60 · A-177⟧ Sans alignement, il n'y a plus d'image de chaîne à déplacer.
                if (!sys.alignement)
                    break;
                const avant = target.channel_image_offset ?? 0;
                const brut = avant + effect.value;
                target.channel_image_offset =
                    effect.plafond === undefined ? brut : Math.min(brut, effect.plafond);
                recalculerImage(target);
                break;
            }
            // ⟦A-84 · SPEC-MOTEUR §7.2bis⟧ **L'axe relationnel d'un PNJ, enfin écrivable par le
            // contenu.** `appliquerRelationRival` existait depuis M3.16 sans un seul appelant hors
            // tests ; le rôle (`cible`) est résolu sur l'état, puisque le contenu ne connaît pas
            // les PNJ tirés. Aucune jauge, aucun abonné : la relation est un miroir, et l'écrire ne
            // déplace rien d'autre qu'elle-même — c'est ce qui la rend neutre pour le harnais.
            case "rival_relation_delta": {
                for (const r of ciblesRelation(target, effect.cible ?? "plus_proche")) {
                    appliquerRelationRival(target, r.id, effect.value);
                }
                break;
            }
            case "event_schedule": {
                const liste = contentEffectsMutable(target).scheduled_events;
                const due_h = target.clock_h + effect.delai_h;
                const dejaPrevu = liste.find((s) => s.event_id === effect.event_id);
                if (dejaPrevu) {
                    // Déjà programmé : la première échéance gagne (les 3 choix de
                    // « changement d'algorithme » programment le même événement).
                    dejaPrevu.due_h = Math.min(dejaPrevu.due_h, due_h);
                    scheduled.push({ ...dejaPrevu });
                }
                else {
                    const entree = { event_id: effect.event_id, due_h };
                    liste.push(entree);
                    scheduled.push({ ...entree });
                }
                break;
            }
        }
    }
    // — Plafond −15 % sur le CUMUL de perte d'abonnés (PLAN §4.9) —
    let subsLossCapped = 0;
    if (capPct !== null && subsDelta < 0) {
        // `floor` : le plafond ne peut jamais être dépassé, même d'un abonné.
        const perteMax = Math.floor(abonnesEntree * capPct);
        if (-subsDelta > perteMax) {
            subsLossCapped = -subsDelta - perteMax;
            subsDelta = perteMax === 0 ? 0 : -perteMax; // jamais de −0 dans l'état sérialisé
            log.push({
                turn: target.turn,
                clock_h: target.clock_h,
                famille: "buzz",
                text: `Plafond de dégâts : ${subsLossCapped} abonnés épargnés (−15 % maximum).`,
            });
        }
    }
    if (subsDelta !== 0) {
        target.subscribers += subsDelta;
        if (subsDelta < 0) {
            // §4.7 : les volatils partent en premier.
            target.subs_volatile = Math.max(0, target.subs_volatile + subsDelta);
            events.push({
                kind: "subs_lost",
                data: {
                    subs_lost: -subsDelta,
                    capped: subsLossCapped > 0,
                    source: options.source_id ?? source,
                },
            });
        }
    }
    // Bornes de toutes les jauges (§2.3 pas 9) — `gauge_delta` est « borné par le moteur ».
    appliquerClamps(target);
    return {
        state: target,
        events,
        log,
        subs_delta: subsDelta,
        subs_loss_capped: subsLossCapped,
        reputation_delta: reputationDelta,
        pc_gained: pcGained,
        strikes_added: strikesAdded,
        cards_to_draw: cardsToDraw,
        scheduled,
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// Lectures dérivées — le sucre dont les tâches suivantes auront besoin
// ─────────────────────────────────────────────────────────────────────────────
/** Une capacité est-elle débloquée ? (`unlock`, §2.3) */
export function hasCapability(mods, capability) {
    return mods.unlocked.has(capability);
}
/**
 * Poids de tirage modulé par le CONTENU (`event_weight_mult_*`) — les modulateurs de
 * SPEC-MOTEUR §10.3 (image, SM, hardcore, contre-mesure) sont appliqués par M1.15, pas ici.
 */
export function poidsModuleParContenu(mods, event) {
    return (event.poids *
        (mods.eventWeight.byId[event.id] ?? 1) *
        (mods.eventWeight.byCategory[event.categorie] ?? 1));
}
/** Événements programmés dont l'échéance est atteinte (`event_schedule`, §10.5). */
export function evenementsDus(state) {
    return contentEffects(state).scheduled_events.filter((s) => state.clock_h >= s.due_h);
}
/** Retire un événement programmé consommé (M1.15). MUTE l'état (qui doit être un clone). */
export function consommerEvenementProgramme(state, eventId) {
    const sidecar = state.content_effects;
    if (!sidecar)
        return;
    const i = sidecar.scheduled_events.findIndex((s) => s.event_id === eventId);
    if (i >= 0)
        sidecar.scheduled_events.splice(i, 1);
}
//# sourceMappingURL=effects.js.map